import React, { useState } from "react";
import { CheckCircle, ArrowRight } from "react-feather";
import { useTheme } from "../../theme/ThemeContext";
import { daysUntilDue } from "../../utils/bills";
import { taskBucket, primaryAction, dueText as taskDueText } from "../../tasks/taskUtils";
import { Sheet, Empty, useMoney, formatDate, todayIso } from "../ui";
import { markIncomeReceived } from "./AccountsScreen";
import { usePayBill, billAmountText } from "./PaidAmountSheet";

const billNote = (bill) => {
  const d = daysUntilDue(bill.dueDate);
  if (d < 0) return `Due since ${formatDate(bill.dueDate)} · ${Math.abs(d)} day${d === -1 ? "" : "s"} late`;
  if (d === 0) return "Due today";
  if (d === 1) return "Due tomorrow";
  return `${d} days left · ${formatDate(bill.dueDate)}`;
};

// What the bell shows. "now" items are what the badge counts: unpaid bills that are
// due today or late, tasks waiting on me, and income that should have arrived.
// "soon" is the rest of the week, for a heads-up.
export const reminderItems = ({ bills, income, tasks }) => {
  const today = todayIso();
  const now = [];
  const soon = [];
  for (const b of bills) {
    if (b.status !== "Pending") continue;
    const d = daysUntilDue(b.dueDate);
    if (d <= 0) now.push({ key: `b${b.id}`, kind: "bill", date: b.dueDate, item: b });
    else if (d <= 7) soon.push({ key: `b${b.id}`, kind: "bill", date: b.dueDate, item: b });
  }
  for (const t of tasks) {
    const bucket = taskBucket(t);
    if (bucket === "todo" || bucket === "confirm") now.push({ key: `t${t.id}`, kind: "task", date: t.dueDate, item: t });
  }
  for (const i of income) {
    if (i.status !== "Received" && i.date <= today) now.push({ key: `i${i.id}`, kind: "income", date: i.date, item: i });
  }
  const byDate = (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0);
  return { now: now.sort(byDate), soon: soon.sort(byDate) };
};

const ActionButton = ({ onClick, disabled, children, tone = "positive" }) => {
  const { tokens } = useTheme();
  const colors = tone === "positive" ? [tokens.positiveBg, tokens.positive] : [tokens.neutralSoft, tokens.textPrimary];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "7px 12px", borderRadius: 999, border: "none", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1, fontSize: 12, fontWeight: 700, backgroundColor: colors[0], color: colors[1] }}
    >
      {children}
    </button>
  );
};

const Row = ({ entry, data, tasksApi, goTo, onClose, first, onPay }) => {
  const { tokens, fonts } = useTheme();
  const money = useMoney();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { kind, item } = entry;

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };
  const open = (tab) => { goTo(tab); onClose(); };

  let title;
  let note;
  let amount = null;
  let actions;
  if (kind === "bill") {
    title = item.payee;
    note = `${item.type} · ${billNote(item)}${item.autopay ? " · Autopay" : ""}`;
    amount = `-${billAmountText(item, money)}`;
    actions = (
      <>
        <ActionButton disabled={busy} onClick={() => run(async () => onPay(item))}><CheckCircle size={13} strokeWidth={2.6} /> Mark as paid</ActionButton>
        <ActionButton tone="neutral" onClick={() => open("bills")}>Open <ArrowRight size={13} /></ActionButton>
      </>
    );
  } else if (kind === "income") {
    title = item.source;
    note = `${item.type} · expected ${formatDate(item.date)}`;
    amount = `+${money(item.amount)}`;
    actions = (
      <>
        <ActionButton disabled={busy} onClick={() => run(() => markIncomeReceived(data, item))}><CheckCircle size={13} strokeWidth={2.6} /> Mark as received</ActionButton>
        <ActionButton tone="neutral" onClick={() => open("accounts")}>Open <ArrowRight size={13} /></ActionButton>
      </>
    );
  } else {
    const action = primaryAction(item);
    title = item.title;
    note = `Task · ${taskDueText(item)}`;
    const doIt = () =>
      action.kind === "confirm" ? tasksApi.confirm(item.id) : tasksApi.submit(item.id, "", null);
    actions = (
      <>
        {action && <ActionButton disabled={busy} onClick={() => run(doIt)}><CheckCircle size={13} strokeWidth={2.6} /> {action.kind === "submit" ? "Mark as done" : action.label}</ActionButton>}
        <ActionButton tone="neutral" onClick={() => open("tasks")}>Open <ArrowRight size={13} /></ActionButton>
      </>
    );
  }

  return (
    <div style={{ padding: "12px 0", borderTop: first ? "none" : `1px solid ${tokens.surfaceBorder}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
          <div style={{ fontSize: 12, marginTop: 2, color: kind === "bill" && daysUntilDue(item.dueDate) < 0 ? tokens.negative : tokens.textMuted }}>{note}</div>
        </div>
        {amount && <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", color: kind === "income" ? tokens.positive : tokens.textPrimary }}>{amount}</div>}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>{actions}</div>
      {error && <div role="alert" style={{ marginTop: 6, fontSize: 12, color: tokens.negative }}>{error}</div>}
    </div>
  );
};

// The bell in the top bar: everything that needs a tap from me, with the action
// right there (mark as paid, mark as done, received) so nothing has to be hunted down.
const NotificationsSheet = ({ data, tasksApi, goTo, onClose }) => {
  const { tokens, fonts } = useTheme();
  const { now, soon } = reminderItems({ bills: data.bills, income: data.income, tasks: tasksApi.tasks });
  // A bill whose amount changes each time asks for what was actually paid.
  const payBill = usePayBill(data);

  const heading = (text) => (
    <div style={{ margin: "14px 0 4px", fontFamily: fonts.display, fontSize: 13, fontWeight: 700, letterSpacing: "0.03em", color: tokens.textMuted }}>{text}</div>
  );

  return (
    <Sheet title="Reminders" onClose={onClose}>
      {now.length === 0 && soon.length === 0 && <Empty>You're all caught up. Nothing is due.</Empty>}
      {now.length > 0 && (
        <>
          {heading("NEEDS ATTENTION")}
          {now.map((e, i) => <Row key={e.key} entry={e} first={i === 0} data={data} tasksApi={tasksApi} goTo={goTo} onClose={onClose} onPay={payBill.pay} />)}
        </>
      )}
      {soon.length > 0 && (
        <>
          {heading("LATER THIS WEEK")}
          {soon.map((e, i) => <Row key={e.key} entry={e} first={i === 0} data={data} tasksApi={tasksApi} goTo={goTo} onClose={onClose} onPay={payBill.pay} />)}
        </>
      )}
      {payBill.sheet}
    </Sheet>
  );
};

export default NotificationsSheet;
